import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Camera, Check, Eye, EyeOff, KeyRound, Loader2, Trash2, TriangleAlert, Archive, Download, Upload, BellRing } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, changePassword, exportMyData, importMyData } from '../../lib/authApi';
import { downloadBlob } from '../../lib/renderUtils';
import { apiErrorMessage } from '../../lib/projectApi';
import { AVATAR_COLORS, avatarColorCss, avatarSrc, fileToAvatarDataUrl, initialsOf } from '../../lib/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Switch } from '../ui/switch';

/**
 * Account ▸ Profile settings: display name, avatar (upload a picture — resized in the browser — or pick an accent colour
 * for the initials) and password (change it, or set a first one on a Google-only account). Profile fields save together
 * with the Save button; the password has its own form so a typo there never blocks a name change.
 */
/** Optional emails are opt-in: one switch, saved on the spot. Account emails (receipts, resets, billing) are not optional. */
const EmailPreferences = () => {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const tips = !!user?.email_prefs?.tips;
  const toggle = async (next) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await updateProfile({ email_tips: next });
      setUser(updated);
      toast.success(next ? 'You are in — tips & product updates are on' : 'Tips & product updates are off', { description: next ? 'Occasional, and every one has an unsubscribe link.' : 'Account emails like receipts and password resets still arrive.', id: 'email-prefs' });
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'The preference could not be saved.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3" data-testid="profile-email-prefs">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground"><BellRing className="h-4 w-4 text-primary" aria-hidden="true" />Email preferences</div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Label htmlFor="profile-email-tips" className="text-sm font-medium text-foreground cursor-pointer">Tips &amp; product updates</Label>
          <p className="mt-0.5 text-xs text-muted-foreground">Occasional emails about new features and ways to get more out of WannaPublish. Off unless you turn it on; unsubscribe any time from the email or here.</p>
        </div>
        <Switch id="profile-email-tips" checked={tips} onCheckedChange={toggle} disabled={busy} aria-label="Tips and product updates" data-testid="profile-email-tips-switch" />
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="profile-email-prefs-note">Account emails — receipts, password resets and billing notices — are always sent.</p>
    </div>
  );
};

export const ProfileDialog = ({ open, onOpenChange }) => {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState(undefined); // undefined = unchanged, '' = remove, data URL = new upload
  const [color, setColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setName(user.name || '');
    setAvatar(undefined);
    setColor(user.avatar_color || '');
    setCurrent(''); setNext(''); setConfirm(''); setPwError(''); setShowPw(false);
  }, [open, user]);

  if (!user) return null;

  const hasPassword = (user.providers || []).includes('password');
  const previewUser = { ...user, name, avatar: avatar === undefined ? user.avatar : (avatar || null), avatar_color: color || null };
  const previewSrc = avatarSrc(previewUser);
  const dirty = name.trim() !== (user.name || '') || avatar !== undefined || (color || '') !== (user.avatar_color || '');

  const pickFile = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      setAvatar(await fileToAvatarDataUrl(file));
    } catch (e) {
      toast.error(e.message || 'That picture could not be used.');
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Enter a display name.'); return; }
    setSaving(true);
    try {
      const patch = { name: name.trim(), avatar_color: color || '' };
      if (avatar !== undefined) patch.avatar = avatar; // '' removes, data URL replaces
      const me = await updateProfile(patch);
      setUser(me);
      toast.success('Profile saved', { id: 'profile' });
      onOpenChange(false);
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'The profile could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const mismatch = confirm.length > 0 && confirm !== next;
  const canChange = next.length >= 8 && confirm === next && (!hasPassword || current.length > 0) && !pwBusy;
  const submitPassword = async (e) => {
    e.preventDefault();
    if (!canChange) return;
    setPwError('');
    setPwBusy(true);
    try {
      const me = await changePassword({ currentPassword: hasPassword ? current : null, newPassword: next });
      setUser(me);
      setCurrent(''); setNext(''); setConfirm('');
      toast.success(hasPassword ? 'Password changed' : 'Password set', { description: 'Other devices were signed out; you stay signed in here.', id: 'profile-pw' });
    } catch (err) {
      setPwError(await apiErrorMessage(err, 'The password could not be changed.'));
    } finally {
      setPwBusy(false);
    }
  };

  const field = 'h-9 text-sm';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Profile settings</DialogTitle>
          <DialogDescription>How you appear in WannaPublish. Your publications, presets and fonts stay with this account.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Avatar + name */}
          <div className="flex items-start gap-4">
            <div className="flex flex-col items-center gap-2">
              <Avatar className="h-20 w-20 ring-1 ring-border" data-testid="profile-avatar-preview">
                {previewSrc && <AvatarImage src={previewSrc} alt="" referrerPolicy="no-referrer" />}
                <AvatarFallback className="text-xl font-semibold text-white" style={{ backgroundColor: avatarColorCss(previewUser.avatar_color) || 'hsl(var(--primary))' }}>{initialsOf(previewUser)}</AvatarFallback>
              </Avatar>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="profile-avatar-file-input" />
              <div className="flex gap-1">
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => fileRef.current?.click()} disabled={reading || saving} data-testid="profile-avatar-upload">{reading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1" aria-hidden="true" />}{previewSrc ? 'Change' : 'Upload'}</Button>
                {(previewUser.avatar) && <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={() => setAvatar('')} disabled={saving} aria-label="Remove picture" title="Remove picture" data-testid="profile-avatar-remove"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
            <div className="flex-1 min-w-0 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="profile-name" className="text-xs">Display name</Label>
                <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Your name" className={field} data-testid="profile-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <p className="text-sm text-muted-foreground truncate" data-testid="profile-email">{user.email}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Initials colour</Label>
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Initials colour" data-testid="profile-color-swatches">
                  {AVATAR_COLORS.map((c) => {
                    const selected = color === c.id;
                    return (
                      <button key={c.id} type="button" role="radio" aria-checked={selected} aria-label={c.label} title={c.label} onClick={() => setColor(selected ? '' : c.id)} className={`h-7 w-7 rounded-full flex items-center justify-center ring-offset-2 ring-offset-background transition-shadow duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'ring-2 ring-foreground' : 'hover:ring-2 hover:ring-border'}`} style={{ backgroundColor: c.bg }} data-testid={`profile-color-${c.id}`}>
                        {selected && <Check className="h-3.5 w-3.5 text-white" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">Shown behind your initials when there is no picture.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="profile-cancel">Cancel</Button>
            <Button type="button" onClick={save} disabled={!dirty || saving || !name.trim()} data-testid="profile-save">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save</Button>
          </div>

          <Separator />

          {/* Password */}
          <form onSubmit={submitPassword} className="space-y-3" noValidate data-testid="profile-password-form">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground"><KeyRound className="h-4 w-4 text-primary" aria-hidden="true" />{hasPassword ? 'Change password' : 'Set a password'}</div>
            {!hasPassword && <p className="text-xs text-muted-foreground">You sign in with Google. Add a password to be able to sign in with your email as well.</p>}
            {hasPassword && (
              <div className="space-y-1.5">
                <Label htmlFor="profile-current-pw" className="text-xs">Current password</Label>
                <Input id="profile-current-pw" type={showPw ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" className={field} aria-invalid={!!pwError || undefined} data-testid="profile-current-password" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="profile-new-pw" className="text-xs">New password</Label>
                <div className="relative">
                  <Input id="profile-new-pw" type={showPw ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" minLength={8} className={`${field} pr-9`} data-testid="profile-new-password" />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={showPw ? 'Hide passwords' : 'Show passwords'} data-testid="profile-password-toggle">{showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="profile-confirm-pw" className="text-xs">Confirm</Label>
                <Input id="profile-confirm-pw" type={showPw ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" placeholder="Type it again" className={field} aria-invalid={mismatch || undefined} data-testid="profile-confirm-password" />
              </div>
            </div>
            {mismatch && <p className="text-xs text-destructive" role="alert" data-testid="profile-password-mismatch">The two passwords do not match.</p>}
            {pwError && <p className="text-xs text-destructive" role="alert" data-testid="profile-password-error">{pwError}</p>}
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground">Other devices are signed out afterwards.</p>
              <Button type="submit" variant="outline" disabled={!canChange} data-testid="profile-password-submit">{pwBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{hasPassword ? 'Change password' : 'Set password'}</Button>
            </div>
          </form>

          <Separator />

          {/* ---- Email preferences ---- */}
          <EmailPreferences />

          <Separator />

          {/* ---- Your data ---- */}
          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3" data-testid="profile-your-data">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Archive className="h-4 w-4 text-primary" aria-hidden="true" />Your data</div>
            <p className="text-xs text-muted-foreground">Download everything in your account as one zip: every publication as a project file (File ▸ Open can read them back), your templates, fonts, schemes, presets, folders and profile — or bring such a zip back into this account.</p>
            <div className="flex flex-wrap gap-2">
              <ExportDataButton />
              <ImportDataButton />
            </div>
          </div>

          <Separator />

          {/* ---- Danger zone ---- */}
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2" data-testid="profile-danger-zone">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground"><TriangleAlert className="h-4 w-4 text-destructive" aria-hidden="true" />Delete account</div>
            <p className="text-xs text-muted-foreground">Permanently removes your account, every publication, template, font, scheme and preset in it. Copies other people saved from your share links stay with them. This cannot be undone.</p>
            <Button type="button" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => setDeleteOpen(true)} data-testid="profile-delete-account">
              <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />Delete my account…
            </Button>
          </div>
        </div>
      </DialogContent>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} hasPassword={hasPassword} email={user.email} onDeleted={() => { setDeleteOpen(false); onOpenChange(false); }} />
    </Dialog>
  );
};

/** "Download everything (.zip)" — streams the account export and saves it; variant 'link' is the inline form used in the delete dialog. */
export const ExportDataButton = ({ variant = 'button' }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { blob, filename } = await exportMyData();
      downloadBlob(blob, filename);
      toast.success('Your data is downloading', { description: filename, id: 'export-data' });
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'The export could not be prepared.'));
    } finally { setBusy(false); }
  };
  if (variant === 'link') {
    return <button type="button" onClick={run} disabled={busy} className="underline underline-offset-2 text-primary hover:text-primary/80 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm" data-testid="export-data-link">{busy ? 'Preparing…' : 'Download your data'}</button>;
  }
  return (
    <Button type="button" variant="outline" onClick={run} disabled={busy} data-testid="export-data-button">
      {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4 mr-2" aria-hidden="true" />}{busy ? 'Preparing your zip…' : 'Download everything (.zip)'}
    </Button>
  );
};

const IMPORT_LABELS = { publications: 'publications', folders: 'folders', templates: 'templates', blocks: 'building blocks', fonts: 'fonts' };

/** "Import a zip…" — uploads an export zip and shows what came in (nothing already here is overwritten). */
export const ImportDataButton = () => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || busy) return;
    if (!/\.zip$/i.test(file.name) && file.type !== 'application/zip' && file.type !== 'application/x-zip-compressed') {
      toast.error('Choose the .zip you downloaded from “Your data”.');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await importMyData(file);
      setResult(r);
      const added = ['publications', 'folders', 'templates', 'blocks', 'fonts'].map((k) => [k, r[k]?.added || 0]).filter(([, n]) => n > 0);
      const merged = Object.values(r.merged || {}).reduce((a, b) => a + b, 0);
      const headline = added.length ? added.map(([k, n]) => `${n} ${IMPORT_LABELS[k]}`).join(', ') : 'Nothing new to add';
      toast.success(added.length ? `Imported ${headline}` : headline, { description: added.length ? 'Open File ▸ Open to see them.' : 'Everything in that zip is already in your account.', id: 'import-data' });
      window.dispatchEvent(new CustomEvent('wp:library-changed', { detail: { merged } }));
    } catch (err) {
      toast.error(await apiErrorMessage(err, 'The zip could not be imported.'));
    } finally { setBusy(false); }
  };
  const parts = result ? [
    ...['publications', 'folders', 'templates', 'blocks', 'fonts'].map((k) => {
      const v = result[k] || {};
      const extra = v.skipped ? `${v.skipped} already here` : v.reused ? `${v.reused} reused` : '';
      return (v.added || v.skipped || v.reused) ? `${IMPORT_LABELS[k]}: ${v.added || 0} added${extra ? ` (${extra})` : ''}` : null;
    }).filter(Boolean),
    result.business_info === 'added' ? 'business information: added' : result.business_info === 'kept' ? 'business information: kept yours' : null,
    Object.values(result.merged || {}).reduce((a, b) => a + b, 0) ? `favourites, schemes and presets merged: ${Object.values(result.merged).reduce((a, b) => a + b, 0)}` : null,
  ].filter(Boolean) : [];
  return (
    <>
      <input ref={fileRef} type="file" accept=".zip,application/zip,application/x-zip-compressed" className="hidden" onChange={onFile} data-testid="import-data-file-input" />
      <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-data-button">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4 mr-2" aria-hidden="true" />}{busy ? 'Importing…' : 'Import a zip…'}
      </Button>
      {result && (
        <div className="basis-full text-xs text-muted-foreground space-y-1" data-testid="import-data-summary">
          {parts.length ? <ul className="list-disc pl-4 space-y-0.5">{parts.map((t) => <li key={t}>{t}</li>)}</ul> : <p>Nothing new was found in that zip.</p>}
          {result.warnings?.length > 0 && (
            <details className="text-[11px]"><summary className="cursor-pointer">{result.warnings.length} note{result.warnings.length === 1 ? '' : 's'}</summary><ul className="list-disc pl-4 mt-1 space-y-0.5">{result.warnings.map((w) => <li key={w}>{w}</li>)}</ul></details>
          )}
        </div>
      )}
    </>
  );
};

/** Confirmation for deleting the account: the password for password accounts, the word DELETE for Google-only ones. */
const DeleteAccountDialog = ({ open, onOpenChange, hasPassword, email, onDeleted }) => {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (!open) { setValue(''); setError(''); setBusy(false); } }, [open]);
  const ready = hasPassword ? value.length > 0 : value.trim().toUpperCase() === 'DELETE';

  const confirm = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      await deleteAccount(hasPassword ? { password: value } : { confirm: value });
      toast.success('Your account was deleted', { description: 'Everything in it has been removed.', id: 'auth' });
      onDeleted?.();
      navigate('/', { replace: true });
    } catch (e) {
      setError(await apiErrorMessage(e, 'The account could not be deleted.'));
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-account-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your account?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>This permanently deletes <span className="font-medium text-foreground">{email}</span> and everything in it:</p>
              <ul className="list-disc pl-5 space-y-0.5 text-xs">
                <li>all your publications (shared links stop working)</li>
                <li>custom templates, building blocks and business information</li>
                <li>uploaded fonts, font favourites, font schemes and gradient presets</li>
              </ul>
              <p className="text-xs">Copies other people already saved from your share links stay with them.</p>
              <p className="text-xs flex items-center gap-1.5 text-foreground"><Download className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />Want a copy first? <ExportDataButton variant="link" /></p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="delete-account-confirm" className="text-xs">{hasPassword ? 'Enter your password to confirm' : 'Type DELETE to confirm'}</Label>
          <Input id="delete-account-confirm" type={hasPassword ? 'password' : 'text'} value={value} onChange={(e) => setValue(e.target.value)} autoComplete={hasPassword ? 'current-password' : 'off'} placeholder={hasPassword ? 'Your password' : 'DELETE'} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }} aria-invalid={!!error || undefined} data-testid="delete-account-confirm-input" />
          {error && <p role="alert" className="text-xs text-destructive" data-testid="delete-account-error">{error}</p>}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="delete-account-cancel">Keep my account</AlertDialogCancel>
          <Button type="button" variant="destructive" onClick={confirm} disabled={!ready || busy} data-testid="delete-account-confirm">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />}{busy ? 'Deleting…' : 'Delete everything'}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
